import { type ShelterTask, shelterTasksFor } from '@shkola/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakButton } from '../components/SpeakButton';
import { GRAD, INK, INK_FAINT, INK_SOFT, PAPER, PillIcon, grad, pillClass } from '../design/forms';
import { ArrowCounterClockwiseIcon, CheckIcon } from '../design/icons';

/*
 * «Завдання в укритті» in «Форми 2» (28.10): the kind and the minutes as a soft label, the title big and
 * light, the text to read (or to hear), the questions with their answers as pills that check themselves —
 * the right one mint with a tick, a wrong one crossed out on the paper.
 */

/** One task: the text, then questions that check themselves at a tap. */
function Task({ task }: { task: ShelterTask }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<Record<number, number>>({});
  const done = Object.keys(picked).length === task.questions.length;
  const right = task.questions.filter((q, i) => picked[i] === q.correct).length;
  return (
    <div className="flex flex-col gap-4">
      <div>
        <span
          className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium"
          style={{ backgroundImage: grad('sky'), color: GRAD.sky.ink }}
        >
          {t(`shelter.kinds.${task.kind}`)} · {t('shelter.minutes', { count: task.minutes })}
        </span>
        <h3
          className="mt-2 font-display text-[26px] leading-8 font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {task.title}
        </h3>
      </div>
      <div className="flex flex-col gap-2 text-[16px] leading-[26px]" style={{ color: INK }}>
        {task.text.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      <div>
        <SpeakButton
          text={[
            task.title,
            ...task.text,
            ...task.questions.map((q) => `${q.text} ${q.options.join(', ')}`),
          ].join('. ')}
        />
      </div>
      <ol className="flex flex-col gap-4">
        {task.questions.map((q, i) => {
          const choice = picked[i];
          return (
            <li key={q.text} className="flex flex-col gap-2">
              <p className="text-[15px] leading-[22px] font-medium" style={{ color: INK }}>
                {i + 1}. {q.text}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {q.options.map((option, o) => {
                  const answered = choice !== undefined;
                  const isRight = answered && o === q.correct;
                  const isWrong = answered && o === choice && o !== q.correct;
                  return (
                    <button
                      key={option}
                      type="button"
                      disabled={answered}
                      onClick={() => setPicked((p) => ({ ...p, [i]: o }))}
                      className={`inline-flex min-h-10 items-center gap-1.5 rounded-full px-4 py-2 text-left text-[14px] transition-colors ${
                        isRight
                          ? 'font-medium'
                          : isWrong
                            ? 'line-through'
                            : answered
                              ? ''
                              : 'hover:bg-[#E9E5DC]'
                      }`}
                      style={
                        isRight
                          ? { backgroundImage: grad('mint'), color: GRAD.mint.ink }
                          : {
                              backgroundColor: PAPER,
                              color: isWrong ? INK_FAINT : answered ? INK_SOFT : INK,
                            }
                      }
                    >
                      {isRight && <CheckIcon size={13} weight="bold" aria-hidden />}
                      {option}
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>
      {done && (
        <p
          className="motion-rise font-display text-[24px] leading-[30px] font-light tracking-[-0.02em]"
          style={{ color: INK }}
        >
          {t('shelter.score', { right, total: task.questions.length })}{' '}
          {right === task.questions.length ? t('shelter.praiseAll') : t('shelter.praiseSome')}
        </p>
      )}
    </div>
  );
}

/** «Завдання в укритті»: tasks of the student's age group, one after another; no internet needed. */
export function ShelterTasks({ grade }: { grade: number | undefined }) {
  const { t } = useTranslation();
  const tasks = shelterTasksFor(grade);
  const [index, setIndex] = useState(0);
  const task = tasks[index % tasks.length];
  if (!task) return null;
  return (
    <div className="flex flex-col gap-5">
      <p className="text-[14px] leading-5" style={{ color: INK_SOFT }}>
        {t('shelter.hint')}
      </p>
      <Task key={task.id} task={task} />
      {tasks.length > 1 && (
        <div>
          <button
            type="button"
            onClick={() => setIndex((n) => n + 1)}
            className={pillClass('dark')}
          >
            {t('shelter.another')}
            <PillIcon icon={ArrowCounterClockwiseIcon} />
          </button>
        </div>
      )}
    </div>
  );
}
